/**
 * EMA Checkout Gate — Livestock Delivery Date
 * =============================================
 * Loaded on every page. Intercepts ALL checkout actions and shows a delivery
 * date picker modal when:
 *   a) the cart contains livestock (fish / coral / invert), AND
 *   b) no delivery date has been saved to cart attributes yet.
 *
 * Uses a custom calendar widget (not native <input type="date">) so unavailable
 * days are visually greyed out on every browser and device.
 *
 * Intercepted paths:
 *   1. Form submits containing [name="checkout"]  (cart page + cart drawer)
 *   2. <a href="...checkout..."> link clicks       (any page)
 *   3. Dynamic checkout button containers          (Shop Pay etc.)
 */

(function () {
  'use strict';
  if (window.__emaCheckoutGateInstalled) return;
  window.__emaCheckoutGateInstalled = true;

  // ── Constants ─────────────────────────────────────────────────────────────────
  var LIVESTOCK_TYPES       = ['fish', 'coral', 'invert', 'livestock', 'anemone'];
  var MIN_DAYS_AHEAD        = 1;
  var MAX_DAYS_AHEAD        = 8;
  var EXCLUDED_DAYS         = [0, 1, 2, 6]; // Sun, Mon, Tue, Sat
  var COURIER_DISPATCH_DAYS = [2, 3, 4];   // Tue, Wed, Thu

  // ── Specific blocked delivery dates (YYYY-MM-DD) ─────────────────────────
  var BLOCKED_DATES = [
    '2026-05-19', // Closed — no deliveries
  ];
  var MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
  var DAY_NAMES   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  // ── State ─────────────────────────────────────────────────────────────────────
  var modalEl           = null;
  var pendingAction     = null;
  var gateCalSelected   = null; // Date | null
  var gateCalViewYear   = null;
  var gateCalViewMonth  = null;
  var previousFocus = null;
  var previousOverflow = null;
  var checking = false;
  var saving = false;
  var modalOpen = false;
  var retryCheckout = null;
  var gateRouteRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || "/";
  function cartRoute(path) { return gateRouteRoot.replace(/\/$/, "") + path; }

  // ── Date helpers ──────────────────────────────────────────────────────────────
  function pad(n) { return String(n).padStart(2, '0'); }

  function today() {
    var parts = new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    var value = {};
    parts.forEach(function (part) { value[part.type] = part.value; });
    return new Date(Number(value.year), Number(value.month) - 1, Number(value.day));
  }

  function addDays(base, n) {
    var d = new Date(base); d.setDate(d.getDate() + n); return d;
  }

  function toInputValue(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function toHumanDate(d) {
    return DAY_NAMES[d.getDay()] + ', ' + d.getDate() + ' ' + MONTH_NAMES[d.getMonth()] + ' ' + d.getFullYear();
  }

  function toShortDate(d) {
    return d.getDate() + ' ' + MONTH_NAMES[d.getMonth()] + ' ' + d.getFullYear();
  }

  function minDate() {
    var d = addDays(today(), MIN_DAYS_AHEAD);
    while (EXCLUDED_DAYS.indexOf(d.getDay()) !== -1) d = addDays(d, 1);
    return d;
  }

  function maxDate() { return addDays(today(), MAX_DAYS_AHEAD); }

  function isValidDeliveryDay(d) {
    if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return false;
    if (d < minDate() || d > maxDate() || EXCLUDED_DAYS.indexOf(d.getDay()) !== -1) return false;
    return BLOCKED_DATES.indexOf(toInputValue(d)) === -1;
  }

  function parseSavedDate(value) {
    if (typeof value !== 'string') return null;
    var text = value.trim();
    var year, month, day;
    var iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) { year = Number(iso[1]); month = Number(iso[2]) - 1; day = Number(iso[3]); }
    else {
      var human = text.match(/^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday), (\d{1,2}) ([A-Za-z]+) (\d{4})$/);
      if (!human) return null;
      year = Number(human[4]); month = MONTH_NAMES.indexOf(human[3]); day = Number(human[2]);
      if (month < 0) return null;
    }
    var date = new Date(year, month, day);
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
    if (!iso && DAY_NAMES[date.getDay()] !== human[1]) return null;
    return date;
  }

  function courierDispatchDate(deliveryDate) {
    var d = addDays(deliveryDate, -1);
    return COURIER_DISPATCH_DAYS.indexOf(d.getDay()) !== -1 ? d : null;
  }

  function isLivestockType(pt) {
    if (!pt) return false;
    var lower = pt.toLowerCase();
    return LIVESTOCK_TYPES.some(function (t) { return lower.indexOf(t) !== -1; });
  }

  function normaliseDeliveryMethod(value) {
    var lower = String(value || '').trim().toLowerCase();
    if (lower === 'pickup') return 'pickup';
    if (lower === 'shipping') return 'shipping';
    return '';
  }

  // ── Cart checks ───────────────────────────────────────────────────────────────

  function fetchJSON(path, options) {
    return fetch(cartRoute(path), options).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    });
  }

  function checkCart() {
    return fetchJSON('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) throw new Error('Cart could not be checked');
        var hasLivestock = cart.items.some(function (item) { return isLivestockType(item.product_type); });
        var attributes = cart.attributes || {};
        var date = parseSavedDate(attributes['EMA Delivery Date ISO'] || attributes['Desired Delivery Date']);
        var deliveryMethod = normaliseDeliveryMethod(attributes['EMA Delivery Method']);
        return {
          hasLivestock: hasLivestock,
          hasDate: isValidDeliveryDay(date),
          deliveryMethod: deliveryMethod
        };
      });
  }

  function saveCartAttributes(deliveryDate) {
    if (!isValidDeliveryDay(deliveryDate)) return Promise.reject(new Error('Choose an available date'));
    var cd = courierDispatchDate(deliveryDate);
    var attributes = {
      'EMA Delivery Method': 'Shipping',
      'EMA Delivery Date ISO': toInputValue(deliveryDate),
      'Desired Delivery Date': toHumanDate(deliveryDate),
      'Dispatch Date (Airfreight)': toShortDate(deliveryDate),
      'Dispatch Date (Overnight Courier)': cd ? toShortDate(cd) : 'N/A – airfreight only for this date'
    };
    return fetchJSON('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (cart) {
      if (!cart || !cart.attributes || Object.keys(attributes).some(function (key) {
        return cart.attributes[key] !== attributes[key];
      })) throw new Error('The delivery date save was not confirmed');
      return cart;
    });
  }

  function synchroniseCartForm(form) {
    if (!form) return Promise.resolve();
    var data = new FormData(form);
    var update = new FormData();
    var count = 0;
    data.forEach(function (value, key) {
      if (key === 'note' || /^updates\[/.test(key) || /^attributes\[/.test(key)) {
        update.append(key, value);
        count++;
      }
    });
    if (!count) return Promise.resolve();
    return fetchJSON('/cart/update.js', {
      method: 'POST', headers: { Accept: 'application/json' }, body: update
    }).then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) throw new Error('Cart update was not confirmed');
      return cart;
    });
  }

  // ── Modal styles ──────────────────────────────────────────────────────────────

  function injectStyles() {
    if (document.getElementById('ema-gate-styles')) return;
    var css = [
      /* Backdrop */
      '.ema-gate-backdrop{position:fixed;inset:0;z-index:99998;background:rgba(0,0,0,.55);',
        'backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);',
        'display:flex;align-items:center;justify-content:center;padding:16px;',
        'opacity:0;transition:opacity .2s ease}',
      '.ema-gate-backdrop.ema-gate--visible{opacity:1}',

      /* Card */
      '.ema-gate-card{background:#fff;border-radius:16px;width:100%;max-width:420px;',
        'box-shadow:0 24px 64px rgba(0,0,0,.25);overflow:hidden;',
        'transform:translateY(14px) scale(.97);',
        'transition:transform .28s cubic-bezier(.34,1.56,.64,1)}',
      '.ema-gate-backdrop.ema-gate--visible .ema-gate-card{transform:translateY(0) scale(1)}',

      /* Card header */
      '.ema-gate-header{display:flex;align-items:center;gap:11px;padding:16px 20px 14px;',
        'background:linear-gradient(135deg,#e8f4fd 0%,#d0e9f7 100%);',
        'border-bottom:1px solid #c4dff0}',
      '.ema-gate-icon{font-size:26px;line-height:1;flex-shrink:0}',
      '.ema-gate-title{font-size:1rem;font-weight:700;color:#0a3a5c;margin:0;line-height:1.2}',
      '.ema-gate-sub{font-size:.78rem;color:#4a7a9b;margin:3px 0 0;line-height:1.35}',

      /* Calendar container */
      '.ema-gate-body{padding:16px 18px 14px}',
      '.ema-gate-cal{}',

      /* Calendar nav */
      '.ema-gate-cal .ema-cal__nav{display:flex;align-items:center;justify-content:space-between;',
        'margin-bottom:10px;padding:0 2px}',
      '.ema-gate-cal .ema-cal__nav-btn{width:34px;height:34px;border-radius:50%;border:none;',
        'background:transparent;font-size:1.35rem;line-height:1;cursor:pointer;',
        'display:flex;align-items:center;justify-content:center;color:#0a3a5c;',
        'transition:background .13s;padding:0;font-family:inherit}',
      '.ema-gate-cal .ema-cal__nav-btn:hover:not([disabled]){background:rgba(10,79,110,.09)}',
      '.ema-gate-cal .ema-cal__nav-btn[disabled]{opacity:.25;cursor:default}',
      '.ema-gate-cal .ema-cal__month-label{font-size:.9rem;font-weight:700;color:#0a3a5c;',
        'letter-spacing:.01em}',

      /* Day-of-week headers */
      '.ema-gate-cal .ema-cal__dow{display:grid;grid-template-columns:repeat(7,1fr);',
        'margin-bottom:3px}',
      '.ema-gate-cal .ema-cal__dow-label{text-align:center;font-size:.66rem;font-weight:700;',
        'letter-spacing:.06em;text-transform:uppercase;color:rgba(10,58,92,.4);padding-bottom:5px}',
      '.ema-gate-cal .ema-cal__dow-label--dim{color:rgba(10,58,92,.2)}',

      /* Grid */
      '.ema-gate-cal .ema-cal__grid{display:grid;grid-template-columns:repeat(7,1fr);gap:2px}',

      /* Day cells */
      '.ema-gate-cal .ema-cal__day{display:flex;align-items:center;justify-content:center;',
        'aspect-ratio:1/1;min-height:36px;font-size:.83rem;font-family:inherit;border:none;',
        'background:transparent;padding:0;line-height:1;border-radius:50%;',
        'transition:background .12s,color .12s,transform .1s}',

      /* Available */
      '.ema-gate-cal .ema-cal__day--available{color:#1a2332;cursor:pointer;font-weight:500}',
      '.ema-gate-cal .ema-cal__day--available:hover{background:rgba(10,79,110,.1);',
        'color:#0a4f6e;transform:scale(1.08)}',
      '.ema-gate-cal .ema-cal__day--available:active{transform:scale(.95)}',

      /* Selected */
      '.ema-gate-cal .ema-cal__day--selected{background:#0A4F6E!important;',
        'color:#fff!important;font-weight:700!important;transform:none!important;',
        'box-shadow:0 2px 8px rgba(10,79,110,.35)}',
      '.ema-gate-cal .ema-cal__day--selected:hover{background:#0A4F6E!important;color:#fff!important}',

      /* Today ring */
      '.ema-gate-cal .ema-cal__day--today:not(.ema-cal__day--selected){',
        'box-shadow:0 0 0 2px #0A4F6E}',

      /* Unavailable */
      '.ema-gate-cal .ema-cal__day--unavailable{color:rgba(0,0,0,.2);',
        'cursor:default;pointer-events:none;font-weight:400}',

      /* Empty */
      '.ema-gate-cal .ema-cal__day--empty{pointer-events:none;visibility:hidden}',

      /* Confirm banner */
      '.ema-gate-selected-display{display:flex;align-items:center;gap:6px;margin-top:11px;',
        'padding:9px 13px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:9px;',
        'font-size:.82em;font-weight:600;color:#166534;line-height:1.4}',
      '.ema-gate-selected-display svg{flex-shrink:0;color:#22c55e}',

      /* Actions */
      '.ema-gate-actions{padding:0 18px 18px}',
      '.ema-gate-btn-confirm{width:100%;background:#0A4F6E;color:#fff;border:none;',
        'border-radius:10px;padding:13px 16px;font-size:.95rem;font-weight:700;',
        'font-family:inherit;cursor:pointer;transition:background .15s,opacity .15s,transform .1s;',
        'letter-spacing:.01em}',
      '.ema-gate-btn-confirm:hover:not(:disabled){background:#1a7fa3}',
      '.ema-gate-btn-confirm:active:not(:disabled){transform:scale(.98)}',
      '.ema-gate-btn-confirm:disabled{opacity:.45;cursor:default}',

      '.ema-gate-backdrop[hidden]{display:none}',
      '.ema-gate-btn-cancel{width:100%;margin-top:8px;padding:10px;border:0;background:transparent;color:#0a3a5c;font:inherit;cursor:pointer}',
      '.ema-gate-error{margin:10px 0;color:#9b1c1c;font-size:.85rem;line-height:1.4}',
      '.ema-gate-card{max-height:calc(100dvh - 32px);overflow-y:auto}',

      /* Shake animation */
      '@keyframes ema-gate-shake{0%,100%{transform:translateX(0)}',
        '20%{transform:translateX(-6px)}40%{transform:translateX(6px)}',
        '60%{transform:translateX(-4px)}80%{transform:translateX(4px)}}',

      /* Mobile compact */
      '@media(max-width:400px){',
        '.ema-gate-card{border-radius:14px}',
        '.ema-gate-body{padding:12px 12px 10px}',
        '.ema-gate-actions{padding:0 12px 14px}',
        '.ema-gate-cal .ema-cal__day{font-size:.75rem;min-height:32px}',
        '.ema-gate-cal .ema-cal__dow-label{font-size:.6rem}',
      '}',
    ].join('');

    var style = document.createElement('style');
    style.id = 'ema-gate-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ── Gate calendar renderer ────────────────────────────────────────────────────

  function renderGateCalendar() {
    var calEl = modalEl && modalEl.querySelector('#ema-gate-cal');
    if (!calEl) return;

    var mn     = minDate();
    var mx     = maxDate();
    var todayD = today();

    if (gateCalViewYear === null) {
      gateCalViewYear  = mn.getFullYear();
      gateCalViewMonth = mn.getMonth();
    }

    var prevLastDay  = new Date(gateCalViewYear, gateCalViewMonth, 0);
    var nextFirstDay = new Date(gateCalViewYear, gateCalViewMonth + 1, 1);
    var canPrev = prevLastDay >= mn;
    var canNext = nextFirstDay <= mx;

    var firstDay   = new Date(gateCalViewYear, gateCalViewMonth, 1);
    var lastDay    = new Date(gateCalViewYear, gateCalViewMonth + 1, 0);
    var startOffset = (firstDay.getDay() + 6) % 7; // Mon=0

    var html = [];

    // Nav
    html.push(
      '<div class="ema-cal__nav">' +
        '<button type="button" class="ema-cal__nav-btn" id="ema-gcal-prev"' +
          (canPrev ? '' : ' disabled') + ' aria-label="Previous month">&#8249;</button>' +
        '<span class="ema-cal__month-label">' +
          MONTH_NAMES[gateCalViewMonth] + ' ' + gateCalViewYear +
        '</span>' +
        '<button type="button" class="ema-cal__nav-btn" id="ema-gcal-next"' +
          (canNext ? '' : ' disabled') + ' aria-label="Next month">&#8250;</button>' +
      '</div>'
    );

    // Day-of-week headers
    var dowLabels = ['Mo','Tu','We','Th','Fr','Sa','Su'];
    var dowDims   = [false, false, false, false, false, true, true];
    html.push('<div class="ema-cal__dow">');
    for (var h = 0; h < 7; h++) {
      html.push('<span class="ema-cal__dow-label' + (dowDims[h] ? ' ema-cal__dow-label--dim' : '') + '">' + dowLabels[h] + '</span>');
    }
    html.push('</div>');

    // Grid
    html.push('<div class="ema-cal__grid">');

    for (var e = 0; e < startOffset; e++) {
      html.push('<span class="ema-cal__day ema-cal__day--empty" aria-hidden="true"></span>');
    }

    for (var d = 1; d <= lastDay.getDate(); d++) {
      var date = new Date(gateCalViewYear, gateCalViewMonth, d);
      date.setHours(0, 0, 0, 0);

      var excluded   = EXCLUDED_DAYS.indexOf(date.getDay()) !== -1;
      var tooEarly   = date < mn;
      var tooLate    = date > mx;
      var available  = isValidDeliveryDay(date);
      var isToday    = date.getTime() === todayD.getTime();
      var isSelected = gateCalSelected && date.getTime() === gateCalSelected.getTime();

      var cls = ['ema-cal__day'];
      if (available)  cls.push('ema-cal__day--available');
      else            cls.push('ema-cal__day--unavailable');
      if (isSelected) cls.push('ema-cal__day--selected');
      if (isToday && available) cls.push('ema-cal__day--today');

      var ds    = toInputValue(date);
      var label = toHumanDate(date) + (available ? '' : ' — not available');

      if (available) {
        html.push(
          '<button type="button" class="' + cls.join(' ') + '"' +
            ' data-date="' + ds + '"' +
            ' aria-label="' + label + '"' +
            (isSelected ? ' aria-pressed="true"' : '') +
          '>' + d + '</button>'
        );
      } else {
        html.push('<span class="' + cls.join(' ') + '" aria-disabled="true">' + d + '</span>');
      }
    }

    var total = startOffset + lastDay.getDate();
    var trail = total % 7 === 0 ? 0 : 7 - (total % 7);
    for (var t = 0; t < trail; t++) {
      html.push('<span class="ema-cal__day ema-cal__day--empty" aria-hidden="true"></span>');
    }

    html.push('</div>');
    calEl.innerHTML = html.join('');

    // Nav bindings
    var prevBtn = calEl.querySelector('#ema-gcal-prev');
    var nextBtn = calEl.querySelector('#ema-gcal-next');
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        gateCalViewMonth--;
        if (gateCalViewMonth < 0) { gateCalViewMonth = 11; gateCalViewYear--; }
        renderGateCalendar();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        gateCalViewMonth++;
        if (gateCalViewMonth > 11) { gateCalViewMonth = 0; gateCalViewYear++; }
        renderGateCalendar();
      });
    }

    // Day bindings
    var dayBtns = calEl.querySelectorAll('.ema-cal__day[data-date]');
    for (var i = 0; i < dayBtns.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var parts = btn.getAttribute('data-date').split('-');
          var sel = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
          sel.setHours(0, 0, 0, 0);
          if (!isValidDeliveryDay(sel) || saving) return;
          gateCalSelected = sel;
          showGateError('');

          // Show selected date below calendar
          var display  = modalEl.querySelector('#ema-gate-selected-display');
          var dispText = modalEl.querySelector('#ema-gate-selected-text');
          var confirmBtn = modalEl.querySelector('#ema-gate-confirm');
          if (display)   { display.hidden = false; }
          if (dispText)  { dispText.textContent = toHumanDate(sel); }
          if (confirmBtn) { confirmBtn.disabled = false; }

          renderGateCalendar();
        });
      })(dayBtns[i]);
    }
  }

  // ── Modal ─────────────────────────────────────────────────────────────────────

  function buildModal() {
    if (modalEl) return;
    injectStyles();

    modalEl = document.createElement('div');
    modalEl.className = 'ema-gate-backdrop';
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-labelledby', 'ema-gate-title');
    modalEl.innerHTML =
      '<div class="ema-gate-card">' +

        // Header
        '<div class="ema-gate-header">' +
          '<span class="ema-gate-icon">🐠</span>' +
          '<div>' +
            '<h2 class="ema-gate-title" id="ema-gate-title">Select Delivery Date</h2>' +
            '<p class="ema-gate-sub">Choose when you\'d like your livestock delivered</p>' +
          '</div>' +
        '</div>' +

        // Calendar
        '<div class="ema-gate-body">' +
          '<div id="ema-gate-cal" class="ema-gate-cal"></div>' +
          '<div id="ema-gate-selected-display" class="ema-gate-selected-display" hidden>' +
            '<svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="4 10 8 14 16 6"/></svg>' +
            '<span id="ema-gate-selected-text"></span>' +
          '</div>' +
        '</div>' +

        // Actions
        '<div class="ema-gate-actions">' +
          '<p id="ema-gate-error" class="ema-gate-error" role="alert" hidden></p>' +
          '<button type="button" class="ema-gate-btn-confirm" id="ema-gate-confirm" disabled>' +
            'Continue to Checkout' +
          '</button>' +
          '<button type="button" class="ema-gate-btn-cancel" id="ema-gate-cancel">Back to shopping</button>' +
        '</div>' +

      '</div>';

    document.body.appendChild(modalEl);

    modalEl.hidden = true;
    modalEl.querySelector('#ema-gate-confirm').addEventListener('click', function () {
      var btn = modalEl.querySelector('#ema-gate-confirm');
      if (retryCheckout && !btn.disabled) {
        var retry = retryCheckout;
        hideModal();
        retry();
        return;
      }
      if (!isValidDeliveryDay(gateCalSelected) || btn.disabled || saving) return;
      saving = true;
      btn.disabled = true;
      btn.textContent = 'Saving…';
      showGateError('');
      saveCartAttributes(gateCalSelected).then(function () {
        if (!modalOpen) return;
        var proceed = pendingAction;
        hideModal();
        if (proceed) proceed();
      }).catch(function () {
        if (modalOpen) showGateError('We could not save your delivery date. Please try again or return to shopping.');
      }).finally(function () {
        saving = false;
        if (modalOpen) { btn.disabled = !isValidDeliveryDay(gateCalSelected); btn.textContent = 'Continue to Checkout'; }
      });
    });
    modalEl.querySelector('#ema-gate-cancel').addEventListener('click', hideModal);
    modalEl.addEventListener('click', function (event) { if (event.target === modalEl) hideModal(); });
    document.addEventListener('keydown', function (event) {
      if (!modalOpen) return;
      if (event.key === 'Escape') { event.preventDefault(); hideModal(); return; }
      if (event.key !== 'Tab') return;
      var buttons = Array.prototype.filter.call(modalEl.querySelectorAll('button:not([disabled])'), function (button) {
        return !button.hidden;
      });
      var first = buttons[0], last = buttons[buttons.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !modalEl.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !modalEl.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }, true);
  }

  function showGateError(message) {
    var node = modalEl && modalEl.querySelector('#ema-gate-error');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  function showModal(onProceed, message, onRetry) {
    buildModal();
    pendingAction = onProceed;
    retryCheckout = onRetry || null;
    modalOpen = true;
    previousFocus = document.activeElement;
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    gateCalSelected = null;
    gateCalViewYear = null;
    gateCalViewMonth = null;
    var display = modalEl.querySelector('#ema-gate-selected-display');
    var confirmBtn = modalEl.querySelector('#ema-gate-confirm');
    if (display) display.hidden = true;
    if (confirmBtn) { confirmBtn.disabled = !retryCheckout; confirmBtn.textContent = retryCheckout ? 'Try again' : 'Continue to Checkout'; }
    showGateError(message || '');
    var mn = minDate();
    gateCalViewYear = mn.getFullYear();
    gateCalViewMonth = mn.getMonth();
    renderGateCalendar();
    modalEl.querySelector('#ema-gate-cal').hidden = !!retryCheckout;
    modalEl.hidden = false;
    modalEl.classList.add('ema-gate--visible');
    modalEl.querySelector('#ema-gate-cancel').focus();
  }

  function hideModal() {
    if (!modalEl) return;
    modalOpen = false;
    pendingAction = null;
    retryCheckout = null;
    modalEl.classList.remove('ema-gate--visible');
    modalEl.hidden = true;
    document.body.style.overflow = previousOverflow || '';
    if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === 'function') previousFocus.focus();
  }

  function attemptCheckout(destination, form) {
    if (checking || modalOpen || saving) return;
    checking = true;
    synchroniseCartForm(form)
      .then(function () { return window.__emaDeliveryStateSave || null; })
      .then(function () { return typeof window.emaEnsureFreeGlue === 'function' ? window.emaEnsureFreeGlue() : null; })
      .then(checkCart)
      .then(function (state) {
      // Pickup is a complete fulfilment path and never needs a delivery date.
      // Legacy carts with a valid date and no method remain valid as Shipping.
      if (!state.hasLivestock || state.deliveryMethod === 'pickup' || state.hasDate) {
        window.location.href = destination;
        return;
      }
      showModal(function () { window.location.href = destination; });
    }).catch(function () {
      showModal(null, 'We could not update or check the cart. Please try again, or return to shopping.', function () {
        attemptCheckout(destination, form);
      });
    }).finally(function () { checking = false; });
  }

  // ── Intercept: form submits ───────────────────────────────────────────────────

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.querySelector) return;
    var submitter = event.submitter;
    if (submitter && submitter.name !== 'checkout') return;
    if (!submitter && !form.querySelector('[name="checkout"]')) return;
    var action = new URL(form.action || '/cart', window.location.origin);
    if (!/\/cart\/?$/.test(action.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    attemptCheckout(cartRoute('/checkout'), form);
  }, true);

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a');
    if (!link || !link.href || link.hostname !== window.location.hostname) return;
    if (!/(?:^|\/)checkouts?(?:\/|$)/.test(link.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    attemptCheckout(link.href);
  }, true);

  // Native accelerated product checkout is left to Shopify. Current product templates disable it.
  // Cart accelerated checkout redirects through the same validated cart checkout owner.
  document.addEventListener('click', function (event) {
    var container = event.target.closest && event.target.closest('.dynamic-cart-btns');
    if (!container) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    attemptCheckout(cartRoute('/checkout'));
  }, true);

})();


